import React, { Fragment, useRef } from 'react';
import { View, TouchableWithoutFeedback, Animated } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { capitalize as _capitalize, isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { isIPhoneDeviceWithNotch } from '@vfgroup-oneplatform/foundation/Utils/Platform';

import FrameworkTestKeys from '../../../../FrameworkTestKeys';
import TobiInterActive from '../../TobiInteractive';
import Tab from '../Tab/Tab';

import styles from './BottomBar.Styles';
import useTobiScale from './useTobiScale';

const isIPhoneWithNotch = isIPhoneDeviceWithNotch();

const renderTabs = (
  trayItems = [],
  tabsAnimation = [],
  activeSubTray = -1,
  isVisible = false,
  onPressTobi = () => { },
  trayPlaceHolders = {},
  tabStyles = {},
  customNumberOfLines,
) => {
  const trayTabs = trayItems.map((trayObject, index) => {
    const {
      notification,
      title,
      icon,
      customStyle,
      customContainerStyle,
      onPress,
      otherProps,
      subTrayID,
    } = trayObject || {};
    const numberOfLines = customNumberOfLines || trayItems.length / 2;
    const customTabContainerStyle = [styles.positionOne];

    return (
      <Tab
        key={`Tab-${index}`}
        notification={notification}
        title={title}
        icon={icon}
        size={32}
        isVisible={isVisible}
        customStyle={customStyle}
        customContainerStyle={[
          customTabContainerStyle[index],
          styles.tabStyle(index, trayItems.length),
          customContainerStyle,
        ]}
        tabAnimation={tabsAnimation[index]}
        onPress={onPress}
        numberOfLines={numberOfLines}
        trayPlaceHolders={trayPlaceHolders}
        accessibilityLabel={`${FrameworkTestKeys.ButtomTray}${_capitalize(subTrayID)}`}
        tabStyles={tabStyles}
        {...otherProps}
      />
    );
  });

  const TobiPressView = !isVisible ? (
    <TouchableWithoutFeedback
      onPress={onPressTobi}
      key="Tobi_Press_View_1"
      testID={FrameworkTestKeys.trayBottomBarTobiViewTWF}
    >
      <View style={styles.injectedView} testID={FrameworkTestKeys.trayBottomBarTobiView} />
    </TouchableWithoutFeedback>
  ) : null;

  trayTabs.splice(trayItems.length / 2, 0, [
    TobiPressView,
    <View
      style={styles.TobiPressView}
      key="Tobi_Press_View_2"
      testID={FrameworkTestKeys.trayBottomBarTobiView2}
    />,
  ]);
  return trayTabs;
};

function onTobiViewLayout({ nativeEvent: { layout } }, getTobiViewHeight) {
  if (_isFunction(getTobiViewHeight)) {
    getTobiViewHeight(layout.height);
  }
}

const BottomBar = ({
  trayItems,
  isVisible,
  extraStyles,
  tabsAnimation,
  activeSubTray,
  onPressTobi,
  trayPlaceHolders,
  trayIcon,
  tabStyles,
  customTrayIconStyle,
  dropTray,
  upTray,
  showModal,
  trayType,
  welcomeMessage,
  welcomeMessageBreakTime,
  notificationsBreakTime,
  notifications,
  positionAnimation,
  getTobiViewHeight,
  customNumberOfLines,
  ...props
}) => {
  const tobiTranslate = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;
  const tobiScaleStyles = useTobiScale({ isVisible, dropTray });

  return (
    <Fragment key="trayView">
      <Animated.View
        style={{ transform: [{ translateY: tobiTranslate.y }], position: 'fixed', bottom: 0 }}
        accessible={false}
        pointerEvents="box-none"
        onLayout={event => onTobiViewLayout(event, getTobiViewHeight)}
        testID={FrameworkTestKeys.trayBottomBarMainAnimatedView}
      >
        <Animated.View
          style={{
            bottom: tobiScaleStyles.bottom,
            transform: [{ scale: tobiScaleStyles.scale }],
          }}
          pointerEvents="box-none"
          testID={FrameworkTestKeys.trayBottomBarAnimatedView}
        >
          <TobiInterActive
            style={[styles.toBiStyle(trayType)]}
            showModal={showModal}
            dropTray={dropTray}
            welcomeMessageBreakTime={welcomeMessageBreakTime}
            notificationsBreakTime={notificationsBreakTime}
            welcomeMessage={welcomeMessage}
            notifications={notifications}
            upTray={upTray}
            trayType={trayType}
            trayIcon={trayIcon}
            customTrayIconStyle={customTrayIconStyle}
          />
        </Animated.View>
      </Animated.View>
      <Animated.View
        style={[
          styles.containerStyle,
          extraStyles,
          isIPhoneWithNotch ? styles.tabsContainerWithNotch : null,
          !isVisible ? positionAnimation : null,
        ]}
        testID={FrameworkTestKeys.ButtomTrayView}
        pointerEvents={isVisible ? 'box-none' : undefined}
      >
        {renderTabs(
          trayItems,
          tabsAnimation,
          activeSubTray,
          isVisible,
          onPressTobi,
          trayPlaceHolders,
          tabStyles,
          customNumberOfLines,
        )}
      </Animated.View>
    </Fragment>
  );
};

BottomBar.defaultProps = {
  theme: LightThemeColors,
  tabsAnimation: [],
};

BottomBar.propTypes = {
  trayItems: PropTypes.array.isRequired,
  tabsAnimation: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.object,
      icon: PropTypes.object,
    }),
  ),
  isVisible: PropTypes.bool.isRequired,
  activeSubTray: PropTypes.number.isRequired,
  images: PropTypes.any,
  extraStyles: ViewPropTypes.style,
  onPressTobi: PropTypes.func,
  trayPlaceHolders: PropTypes.object,
  trayIcon: PropTypes.element,
  tabStyles: PropTypes.shape({
    title: TextPropTypes.style,
    icon: ViewPropTypes.style,
  }),
  customTrayIconStyle: ViewPropTypes.style,
  dropTray: PropTypes.bool,
  upTray: PropTypes.bool,
  showModal: PropTypes.func,
  theme: PropTypes.object,
  welcomeMessage: PropTypes.string,
  welcomeMessageBreakTime: PropTypes.number,
  notificationsBreakTime: PropTypes.number,
  notifications: PropTypes.arrayOf(Object),
  trayType: PropTypes.string,
  positionAnimation: ViewPropTypes.style,
  getTobiViewHeight: PropTypes.func,
  customNumberOfLines: PropTypes.number,
};

export default withTheme(BottomBar);
export { renderTabs, onTobiViewLayout };
